import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Package, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ORDERS_PER_PAGE = 5;

const Orders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, page, sortOrder]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders`, {
        params: { page, limit: ORDERS_PER_PAGE, sort_order: sortOrder }
      });
      setOrders(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleSort = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    setPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A1A1A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="orders-title"
          >
            My Orders
          </motion.h1>

          {total > 0 && (
            <div className="flex items-center justify-between mb-12">
              <p className="text-[#888888] text-sm" data-testid="orders-count">
                {total} order{total !== 1 ? 's' : ''}
              </p>
              <button
                onClick={toggleSort}
                data-testid="orders-sort-btn"
                className="flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#1A1A1A] transition-colors"
              >
                <ArrowUpDown size={14} />
                {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
              </button>
            </div>
          )}

          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-24"
            >
              <Package size={64} className="mx-auto mb-6 text-[#888888]" strokeWidth={1} />
              <h2 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                No orders yet
              </h2>
              <p className="text-[#888888] mb-8">Start shopping to see your orders here</p>
              <button
                onClick={() => navigate('/featured')}
                data-testid="orders-browse-btn"
                className="bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300"
              >
                Browse Artwork
              </button>
            </motion.div>
          ) : (
            <>
            <div className="space-y-8">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-lg border border-black/5"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-black/5">
                    <div>
                      <p className="text-sm uppercase tracking-widest text-[#888888] mb-2">
                        Order #{order.id.slice(0, 8)}
                      </p>
                      <p className="text-sm text-[#888888]">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                      <span
                        className={`inline-block px-4 py-2 rounded-full text-xs uppercase tracking-widest ${
                          order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : order.status === 'confirmed'
                            ? 'bg-blue-100 text-blue-800'
                            : order.status === 'shipped'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="text-[#4A4A4A]">
                          {item.product_name} x{item.quantity}
                        </span>
                        <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between items-center pt-6 border-t border-black/5">
                    <span className="text-lg font-medium">Total</span>
                    <span className="text-2xl font-bold">${order.total_amount.toFixed(2)}</span>
                  </div>

                  <div className="mt-6 p-4 bg-[#F5F5F0] rounded-lg">
                    <p className="text-sm uppercase tracking-widest mb-2 text-[#888888]">Shipping Address</p>
                    <p className="text-sm">
                      {order.shipping_address.full_name}<br />
                      {order.shipping_address.address}<br />
                      {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center justify-center gap-3 mt-12"
                data-testid="orders-pagination"
              >
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  data-testid="orders-pagination-prev"
                  className="flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest border border-black/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    data-testid={`orders-pagination-page-${p}`}
                    className={`w-11 h-11 rounded-full text-sm transition-all duration-300 ${
                      p === page
                        ? 'bg-[#1A1A1A] text-white'
                        : 'border border-black/10 hover:bg-[#1A1A1A] hover:text-white'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  data-testid="orders-pagination-next"
                  className="flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest border border-black/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
                >
                  Next <ChevronRight size={16} />
                </button>
              </motion.div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;
